import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Music, Plus, Save, Trash2, Edit2 } from "lucide-react";

export function MusicTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    name: "",
    description: "",
    stream_url: "",
    is_active: true,
    order: 0
  });

  const load = async () => {
    try {
      const r = await api.get("/music-stations/admin");
      setItems(r.data || []);
    } catch (e) {
      console.error("Music load error", e);
      toast.error("Failed to load music stations");
    } finally {
      setLoading(false);
    }
  };

  const seed = async () => {
    if (!window.confirm("Import default music stations?")) return;
    try {
      await api.post("/music-stations/seed");
      toast.success("Default stations imported");
      await load();
    } catch (e) {
      toast.error("Import failed");
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.stream_url.trim()) {
      toast.error("Name and Stream URL are required");
      return;
    }

    try {
      if (editingId) {
        await api.put(`/music-stations/${editingId}`, form);
        toast.success("Station updated");
      } else {
        await api.post("/music-stations", form);
        toast.success("Station added");
      }
      setForm({ name: "", description: "", stream_url: "", is_active: true, order: items.length });
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const edit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name,
      description: item.description || "",
      stream_url: item.streamUrl || item.stream_url,
      is_active: item.is_active,
      order: item.order
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const del = async (id) => {
    if (!window.confirm("Delete this station?")) return;
    try {
      await api.delete(`/music-stations/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  if (loading) return <div className="p-10 text-center text-[#5C6661]">Loading music stations...</div>;

  return (
    <div className="space-y-8" data-testid="music-tab">
      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl shadow-sm">
        <div className="flex justify-between items-start mb-4">
          <h3 className="font-serif-display text-2xl text-[#1C201E] flex items-center gap-2">
            <Music className="w-6 h-6 text-[#2C4C3B]" /> {editingId ? "Edit Music Station" : "Add Music Station"}
          </h3>
          <button
            onClick={seed}
            className="text-xs px-3 py-1.5 rounded-full border border-[#D4A373] text-[#8a5a2a] hover:bg-[#D4A373] hover:text-white transition-colors"
          >
            Import Default Stations
          </button>
        </div>

        <form onSubmit={save} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-bold text-[#5C6661]">Station Name</label>
              <input
                type="text"
                placeholder="e.g. Instrumental"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-[#E5E0D8] focus:ring-2 focus:ring-[#D4A373] outline-none text-sm"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold text-[#5C6661]">Stream URL</label>
              <input
                type="text"
                placeholder="https://..."
                value={form.stream_url}
                onChange={e => setForm({ ...form, stream_url: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-[#E5E0D8] focus:ring-2 focus:ring-[#D4A373] outline-none text-sm"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-bold text-[#5C6661]">Description</label>
            <input
              type="text"
              placeholder="Briefly describe the music style"
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-[#E5E0D8] focus:ring-2 focus:ring-[#D4A373] outline-none text-sm"
            />
          </div>

          <div className="flex items-center gap-6">
            <div className="space-y-1 w-24">
              <label className="text-xs font-bold text-[#5C6661]">Order</label>
              <input
                type="number"
                value={form.order}
                onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 rounded-lg border border-[#E5E0D8] focus:ring-2 focus:ring-[#D4A373] outline-none text-sm"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer mt-5">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={e => setForm({ ...form, is_active: e.target.checked })}
                className="rounded border-[#E5E0D8] text-[#2C4C3B] focus:ring-[#D4A373]"
              />
              <span className="text-xs text-[#1C201E]">Visible in App</span>
            </label>
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 px-6 py-2 rounded-full bg-[#2C4C3B] text-white font-bold hover:bg-[#1A3326] transition-colors"
            >
              {editingId ? <><Save className="w-4 h-4" /> Update</> : <><Plus className="w-4 h-4" /> Add Station</>}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={() => { setEditingId(null); setForm({ name: "", description: "", stream_url: "", is_active: true, order: items.length }); }}
                className="px-6 py-2 rounded-full border border-[#E5E0D8] text-[#5C6661] hover:bg-[#F3EFEA]"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl shadow-sm">
        <h3 className="font-serif-display text-xl text-[#1C201E] mb-4">Manage Stations</h3>
        <div className="divide-y divide-[#E5E0D8]">
          {(items || []).map((item) => (
            <div key={item.id} className="py-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#F3EFEA] rounded-lg">
                  <Music className="w-5 h-5 text-[#2C4C3B]" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-[#1C201E]">{item.name}</span>
                    {!item.is_active && <span className="text-[9px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded font-bold uppercase">Inactive</span>}
                  </div>
                  <p className="text-xs text-[#5C6661]">{item.description}</p>
                  <code className="text-[10px] text-[#2C4C3B]">{item.streamUrl || item.stream_url}</code>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => edit(item)}
                  className="p-2 text-[#2C4C3B] hover:bg-[#F3EFEA] rounded-lg transition-colors"
                  title="Edit"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => del(item.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {(!items || items.length === 0) && <p className="text-center py-6 text-sm text-[#5C6661]">No stations added yet.</p>}
        </div>
      </div>
    </div>
  );
}

export default MusicTab;
